import React from 'react';

import { ArrowRightIcon } from '@radix-ui/react-icons';
import { Avatar, Button, Flex, Text, getAvatarColor } from '@raystack/apsara';

import { ReactComponent as TokenIcon } from '@src/assets/credit_coin.svg';
import { Show } from '@src/hoc';

import styles from './ToolCard.module.css';

interface TokenSection {
  tokens: React.ReactNode | null;
}

interface ThumbnailSection {
  icon?: React.ReactNode;
  src?: string;
  heading: string;
  showButton?: boolean;
  buttonClick?: () => void;
}

interface ToolCard extends ThumbnailSection {
  subHeading?: string;
  showTokens?: boolean;
  tokens?: TokenSection['tokens'];
}

const TokenSection = ({ tokens }: TokenSection) => {
  return (
    <Flex gap='small'>
      <Flex gap='extra-small'>
        <TokenIcon className={styles.Icon} />
        <Text variant='secondary' size={2} weight={400}>
          Token:
        </Text>
      </Flex>
      <Text variant='primary' size={2} weight={400}>
        {tokens}
      </Text>
    </Flex>
  );
};

const ThumbnailSection = ({
  icon,
  src,
  heading,
  showButton = false,
  buttonClick
}: ThumbnailSection) => {
  return (
    <Flex align='start' justify='between' className='fullWidth'>
      <Show
        when={!!icon}
        fallback={
          <Avatar
            src={src}
            size={6}
            radius='small'
            fallback={heading?.charAt?.(0)?.toUpperCase?.()}
            color={getAvatarColor(heading)}
          />
        }
      >
        <Flex
          align='center'
          justify='center'
          className={styles.toolIconWrapper}
        >
          <div className={styles.toolIcon}>{icon}</div>
        </Flex>
      </Show>
      <Show when={showButton}>
        <Button
          size='small'
          variant='outline'
          color='neutral'
          trailingIcon={<ArrowRightIcon />}
          onClick={() => buttonClick?.()}
          data-test-id='view-button-clicked'
        >
          View
        </Button>
      </Show>
    </Flex>
  );
};

export const ToolCard = ({
  src,
  icon,
  heading,
  subHeading,
  showButton = false,
  showTokens = false,
  tokens = null,
  buttonClick
}: ToolCard) => {
  return (
    <Flex className={styles.toolCard} gap={5} direction='column' align='start'>
      <ThumbnailSection
        src={src}
        icon={icon}
        showButton={showButton}
        buttonClick={buttonClick}
        heading={heading}
      />

      <Text size='regular' weight='medium'>
        {heading}
      </Text>
      <Show when={!!subHeading}>
        <Text size='small' variant='secondary'>
          {subHeading}
        </Text>
      </Show>

      <Show when={showTokens && tokens !== null}>
        <TokenSection tokens={tokens} />
      </Show>
    </Flex>
  );
};
